import { Check, ChevronsUpDown } from 'lucide-react';
import { useId, useMemo, useRef, useState } from 'react';
import { cx } from '@/lib/cx';
import { inputClass } from './styles';

/**
 * A searchable single-select (ARIA 1.2 combobox with a listbox popup), used for time zones.
 * Type to filter; arrows move, Enter picks, Escape closes.
 */
export function Combobox({
  label,
  value,
  options,
  onChange,
  format = (v) => v,
  hint,
  max = 80,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (v: string) => void;
  format?: (v: string) => string;
  hint?: string;
  max?: number;
}) {
  const id = useId();
  const listId = `${id}-list`;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase().replace(/\s+/g, '_');
    const list = q ? options.filter((o) => o.toLowerCase().includes(q) || format(o).toLowerCase().includes(query.trim().toLowerCase())) : options;
    return list.slice(0, max);
  }, [options, query, format, max]);

  const pick = (v: string) => {
    onChange(v);
    setQuery('');
    setOpen(false);
  };

  return (
    <div className="relative flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
      </label>
      <div className="relative">
        <input
          ref={inputRef}
          id={id}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open && filtered[active] ? `${id}-opt-${active}` : undefined}
          aria-describedby={hint ? `${id}-hint` : undefined}
          autoComplete="off"
          spellCheck={false}
          className={cx(inputClass, 'pr-9')}
          value={open ? query : format(value)}
          placeholder={format(value)}
          onFocus={() => {
            setOpen(true);
            setQuery('');
            setActive(Math.max(0, options.indexOf(value)));
          }}
          onBlur={() => window.setTimeout(() => setOpen(false), 120)}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setOpen(true);
              setActive((a) => Math.min(a + 1, filtered.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActive((a) => Math.max(a - 1, 0));
            } else if (e.key === 'Enter') {
              if (open && filtered[active]) {
                e.preventDefault();
                pick(filtered[active]);
              }
            } else if (e.key === 'Escape') {
              setOpen(false);
              setQuery('');
            }
          }}
        />
        <ChevronsUpDown className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-muted-ink" aria-hidden />
      </div>
      {hint && (
        <p id={`${id}-hint`} className="text-xs text-muted-ink">
          {hint}
        </p>
      )}
      {open && (
        <ul
          id={listId}
          role="listbox"
          aria-label={label}
          className="absolute top-full z-30 mt-1 max-h-64 w-full overflow-y-auto rounded-card border border-border bg-card p-1 shadow-lg"
        >
          {filtered.length === 0 && <li className="px-3 py-2 text-sm text-muted-ink">No matches</li>}
          {filtered.map((o, i) => (
            <li
              key={o}
              id={`${id}-opt-${i}`}
              role="option"
              aria-selected={o === value}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(o);
              }}
              onMouseEnter={() => setActive(i)}
              className={cx(
                'flex cursor-pointer items-center justify-between gap-2 rounded-[8px] px-3 py-2 text-sm',
                i === active ? 'bg-muted' : '',
              )}
            >
              <span className="truncate">{format(o)}</span>
              {o === value && <Check className="size-4 shrink-0" aria-hidden />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
